import {
  EnvironmentVariables,
  MainSidebarListProps,
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import { Box, ListItem, ListItemText } from "@mui/material";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PathConstants } from "@/routes/constants";
import { CopyCheckIcon } from "@/modules/common/components/ui-kit/icons/copy-check-icon";
import { UserRoundIcon } from "@/modules/common/components/ui-kit/icons/user-round-icon";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { BookUserIcon } from "@/modules/common/components/ui-kit/icons/book-user-icon";
import { BuildingIcon } from "@/modules/common/components/ui-kit/icons/building-icon";
import { TagIcon } from "@/modules/common/components/ui-kit/icons/tag-icon";
import { BankNoteIcon } from "@/modules/common/components/ui-kit/icons/bank-note-icons";
import { Rotate3dIcon } from "@/modules/common/components/ui-kit/icons/rotate-3d-icon";
import { HouseIcon } from "@/modules/common/components/ui-kit/icons/house-icon";
import ClipboardListIcon from "@/modules/common/components/ui-kit/icons/clipboard-list-icon";
import { useContext } from "react";
import { userHasPermission } from "@/modules/common/utils";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

export const MainSidebarList = ({
  getNavLinkStyles,
  handleItemClick,
  StyledListItemButton,
}: MainSidebarListProps) => {
  const companyName = EnvironmentVariables.COMPANY_NAME.toString();
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  const cannotViewPropertyTracks = userHasPermission(
    {
      name: PermissionsEnum.HIDE_PROPERTY_TRACKS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );
  return (
    <>
      <ListItem disablePadding>
        <StyledListItemButton
          selected={getNavLinkStyles(PathConstants.HOME)}
          onClick={() => handleItemClick(PathConstants.HOME)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: nawyTheme.nawy.spacing.xs,
            }}
          >
            <HouseIcon />
            <ListItemText primary="Home" />
          </Box>
        </StyledListItemButton>
      </ListItem>

      {/* Clients */}
      {companyName === OrganizationTierTypes.NAWY && (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.CUSTOMERS,
              level: PermissionLevel.READ,
            },
          ]}
        >
          <ListItem disablePadding>
            <StyledListItemButton
              selected={getNavLinkStyles(PathConstants.CLIENTS)}
              onClick={() => handleItemClick(PathConstants.CLIENTS)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: nawyTheme.nawy.spacing.xs,
                }}
              >
                <UserRoundIcon />
                <ListItemText primary="Clients" />
              </Box>
            </StyledListItemButton>
          </ListItem>
        </PermissionGuard>
      )}
      {/* Leads */}
      <ListItem disablePadding>
        <StyledListItemButton
          selected={getNavLinkStyles(PathConstants.LEADS)}
          onClick={() => handleItemClick(PathConstants.LEADS)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: nawyTheme.nawy.spacing.xs,
            }}
          >
            <BookUserIcon />
            <ListItemText primary="Leads" />
          </Box>
        </StyledListItemButton>
      </ListItem>
      {/* Properties */}
      {companyName === OrganizationTierTypes.NAWY &&
        !cannotViewPropertyTracks && (
          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.TRACKS,
                level: PermissionLevel.READ,
              },
            ]}
          >
            <ListItem disablePadding>
              <StyledListItemButton
                selected={getNavLinkStyles(PathConstants.PROPERTY_TRACKS)}
                onClick={() => handleItemClick(PathConstants.PROPERTY_TRACKS)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: nawyTheme.nawy.spacing.xs,
                  }}
                >
                  <BuildingIcon />
                  <ListItemText primary="Properties" />
                </Box>
              </StyledListItemButton>
            </ListItem>
          </PermissionGuard>
        )}
      {/* Next Actions */}
      <ListItem disablePadding>
        <StyledListItemButton
          selected={getNavLinkStyles(PathConstants.NEXT_ACTIONS)}
          onClick={() => handleItemClick(PathConstants.NEXT_ACTIONS)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: nawyTheme.nawy.spacing.xs,
            }}
          >
            <CopyCheckIcon />
            <ListItemText primary="Next Actions" />
          </Box>
        </StyledListItemButton>
      </ListItem>
      {/* Sales */}
      <ListItem disablePadding>
        <StyledListItemButton
          selected={getNavLinkStyles(PathConstants.SALES)}
          onClick={() => handleItemClick(PathConstants.SALES)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: nawyTheme.nawy.spacing.xs,
            }}
          >
            <TagIcon />
            <ListItemText primary="Sales" />
          </Box>
        </StyledListItemButton>
      </ListItem>
      {/* Commissions */}
      {companyName === OrganizationTierTypes.NAWY && (
        <ListItem disablePadding>
          <StyledListItemButton
            selected={getNavLinkStyles(PathConstants.COMMISSIONS)}
            onClick={() => handleItemClick(PathConstants.COMMISSIONS)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: nawyTheme.nawy.spacing.xs,
              }}
            >
              <BankNoteIcon />
              <ListItemText primary="Commissions" />
            </Box>
          </StyledListItemButton>
        </ListItem>
      )}
      {/* Inventory */}
      <ListItem disablePadding>
        <StyledListItemButton
          selected={getNavLinkStyles(PathConstants.NAWY_INVENTORY)}
          onClick={() => handleItemClick(PathConstants.NAWY_INVENTORY)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: nawyTheme.nawy.spacing.xs,
            }}
          >
            <Rotate3dIcon />
            <ListItemText primary="Full Inventory" />
          </Box>
        </StyledListItemButton>
      </ListItem>
      {/* Task Dashboard */}
      {EnvironmentVariables.COMPANY_NAME.toString() ===
        OrganizationTierTypes.NAWY && (
        <ListItem disablePadding>
          <StyledListItemButton
            selected={getNavLinkStyles(PathConstants.TASK_DASHBOARD)}
            onClick={() => handleItemClick(PathConstants.TASK_DASHBOARD)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: nawyTheme.nawy.spacing.xs,
              }}
            >
              <ClipboardListIcon />
              <ListItemText primary="Task Dashboard" />
            </Box>
          </StyledListItemButton>
        </ListItem>
      )}
    </>
  );
};
